import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useParams, Link, useNavigate } from 'react-router-dom';
import DocumentUpload from './DocumentUpload';
import { trackOnboardingEvent } from '../Onboarding/utils/trackOnboarding';
import { useOnboarding } from '../Onboarding/OnboardingContext';
import { toast } from 'react-toastify';

import {
    BiFile, BiArrowBack, BiEdit, BiDownload, BiCheckCircle
} from 'react-icons/bi';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { API_BASE_URL } from '../utils/urls.js';
import axiosClient from '../utils/apiClient.js';
// import ServerUpdateSection from "./ServerUpdateSection"; // Commented out as in original code
import ServeeActionComponent from "./ServeeActionComponent";
import AffidavitListComponent from "./AffidavitListComponent";
import CaseMessaging from "./CaseMessaging";


// --- Reusable Component for Info Boxes (Optimized for Balanced Single-Page Layout) ---
const DetailBox = ({ title, children, className = '' }) => (
    <div className={`mt-5 print:mt-4 ${className}`}>
        {title && (
            <div className="border-b-2 border-gray-400 pb-1.5 mb-3.5">
                <h3 className="text-base sm:text-lg font-bold uppercase tracking-wider text-black m-0 p-0 leading-normal">
                    {title}
                </h3>
            </div>
        )}
        {children}
    </div>
);

const DetailItem = ({ label, value }) => {
    if (!value) return null;
    const displayValue = Array.isArray(value) || (typeof value === 'string' && value.includes(','))
        ? value.toString().split(',').map((v, i) =>
            <span key={i} className="inline-block text-gray-900 text-sm sm:text-base font-normal mr-1.5 break-words">{v.trim()}</span>
        )
        : value;

    return (
        <p className="text-sm sm:text-base text-gray-800 mb-2.5 flex flex-col sm:flex-row leading-normal">
            <span className="font-bold text-gray-700 mr-2 min-w-[145px] inline-block basis-full sm:basis-auto">{label}:</span>
            <span className="font-normal text-gray-900 break-words">{displayValue}</span>
        </p>
    );
};

// --- Helper Function to determine download type ---
const getDownloadTypeFromPath = (filePath) => {
    if (filePath && filePath.toLowerCase().includes('uploads\\media')) return 'media';
    return 'documents';
};

const CaseDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [caseData, setCaseData] = useState(null);
    const [documents, setDocuments] = useState([]);
    const [media, setMedia] = useState([]);
    const [servees, setServees] = useState([]);
    const [activity, setActivity] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [agents, setAgents] = useState('');
    const [affidavitUpdateTrigger, setAffidavitUpdateTrigger] = useState(0);
    const [currentClientId, setCurrentClientId] = useState(null);
    const printRef = useRef(null);
    const [showSuccessModal, setShowSuccessModal] = useState(false);

    const { runTour, stepIndex, setStepIndex, setRunTour, completeOnboarding } = useOnboarding();

    // AI: AUTO-FILL DUMMY DATA FOR TOUR TO PREVENT BLANK SCREENS
    useEffect(() => {
        if (runTour && stepIndex >= 5 && !caseData) {
            setCaseData({
                id: 'demo',
                case_name: 'Doe vs. Smith - Subpoena',
                case_number: 'DEMO-56468',
                status: 'Open',
                relevant_dates: '2026-06-04',
                client_info: {
                    full_name: 'John Doe (DEMO)',
                    contact_person: 'Jane Smith',
                    phone_number: '555-0199',
                    email_address: 'john@example.com',
                    preferred_contact_method: 'Email'
                }
            });
            setLoading(false);
        }
    }, [runTour, stepIndex, caseData]);


    const formatLabel = (value) => {
        if (!value) return "";
        return value
            .toString()
            .replace(/_/g, " ")
            .toLowerCase()
            .replace(/\b\w/g, (char) => char.toUpperCase());
    };
    // User roles
    const user = JSON.parse(localStorage.getItem('user'));
    const planType = user?.organization?.planType || 'N/A';
    const roleName = user?.roleName;
    const canEdit = roleName === 'admin' || roleName === 'case_manager';
    const canUpload = roleName === 'admin' || roleName === 'case_manager' || roleName === 'field_agent';

    const handleAffidavitCreated = useCallback(() => {
        // Incrementing the state forces the ParentComponent to re-render, 
        // which then forces the AffidavitListComponent to re-render.
        setAffidavitUpdateTrigger(prev => prev + 1);

        toast.success("✅ Your Affidavit has been generated successfully!");
        // (Note: If you are using a library like react-toastify, you can replace this with toast.success("..."))
    }, []);

    const handleFinalRedirect = () => {
        setRunTour(false);
        setStepIndex(0);
        if (typeof completeOnboarding === 'function') completeOnboarding();

        // Redirect based on role
        if (roleName === 'admin' || roleName === 'super_admin') {
            navigate('/dashboard/admin');
        } else if (roleName === 'case_manager') {
            navigate('/dashboard/case-manager');
        } else {
            navigate('/');
        }
    };

    // PDF download logic - Guaranteed Single Page Auto-Fit
    const downloadPdf = async () => {
        const element = printRef.current;
        if (!element) return;

        try {
            const scale = window.innerWidth <= 768 ? 3 : 2;

            const canvas = await html2canvas(element, {
                scale: scale,
                useCORS: true,
                backgroundColor: '#ffffff'
            });
            const imgData = canvas.toDataURL('image/jpeg', 1.0);
            const pdf = new jsPDF('p', 'mm', 'a4');
            const pageWidth = pdf.internal.pageSize.getWidth(); // 210 mm
            const pageHeight = pdf.internal.pageSize.getHeight(); // 297 mm
            const margin = 8; // 8mm margin
            const printableWidth = pageWidth - (margin * 2); // 194 mm
            const printableHeight = pageHeight - (margin * 2); // 281 mm

            const imgProps = pdf.getImageProperties(imgData);
            let renderWidth = printableWidth;
            let renderHeight = (imgProps.height * renderWidth) / imgProps.width;

            // Auto-fit guarantee: if renderHeight exceeds printable height, scale it down proportionally
            if (renderHeight > printableHeight) {
                const ratio = printableHeight / renderHeight;
                renderWidth = renderWidth * ratio;
                renderHeight = printableHeight;
            }

            const xOffset = margin + (printableWidth - renderWidth) / 2;
            const yOffset = margin;

            pdf.addImage(imgData, 'JPEG', xOffset, yOffset, renderWidth, renderHeight);

            const fileName = `IntakeForm-Case-${caseData.case_number || id}.pdf`;
            pdf.save(fileName);
        } catch (error) {
            console.error('Error generating PDF:', error);
            alert('Failed to generate PDF. Please try again.');
        }
    };

    const fetchData = async () => {
        const token = localStorage.getItem('token');
        if (!token) { setError('Authentication token not found.'); setLoading(false); return; }

        try {
            const [caseRes, activityRes] = await Promise.all([
                axiosClient.get(`/cases/${id}`),
                axiosClient.get(`/cases/${id}/activity`),
            ]);

            setCaseData(caseRes.data);
            console.log('Fetched case data:', caseRes.data); // Debug log for case data
            const agentNames = Array.isArray(caseRes.data.assigned_agents)
                ? caseRes.data.assigned_agents.map(a => a.full_name).join(', ')
                : '';
            setAgents(agentNames);
            setDocuments(caseRes.data.documents.reverse());
            setMedia(caseRes.data.media.reverse());
            setServees(caseRes.data.servees);
            setActivity(activityRes.data);
        } catch (err) {
            console.error('Error fetching case details:', err);
            setError(err.response?.data?.message || 'Failed to load case details. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchData(); }, [id]);

    const handleDownload = async (documentId, fileName, type) => {
        const token = localStorage.getItem('token');
        try {
            const response = await axios.get(`${API_BASE_URL}/cases/download/${type}/${documentId}`, {
                responseType: 'blob',
                headers: { Authorization: `Bearer ${token}` }
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
        } catch (err) {
            console.error('Error downloading document:', err);
            alert('Failed to download document.');
        }
    };

    const handleUploadSuccess = () => { fetchData(); };

    if (loading) return <div className="p-10 text-center text-lg text-blue-600">Loading case details...</div>;
    if (error) return <div className="p-10 text-center text-xl text-red-600">{error}</div>;
    if (!caseData) return <div className="p-10 text-center text-lg text-gray-500">Case not found.</div>;

    const { client_info = {}, custom_fields = {} } = caseData;
    // --- NEW: Onboarding Guide Banner Component ---
const OnboardingGuide = ({ step, title, description }) => (
    <div className="bg-indigo-50 border border-indigo-200 p-4 mt-8 mb-4 rounded-xl shadow-sm flex items-start space-x-4 print:hidden animate-fade-in">
        <div className="flex-shrink-0 bg-indigo-600 text-white font-black text-sm h-8 w-8 rounded-full flex items-center justify-center shadow-md">
            {step}
        </div>
        <div>
            <h4 className="text-indigo-900 font-bold text-sm sm:text-base">{title}</h4>
            <p className="text-indigo-700 text-xs sm:text-sm mt-1">{description}</p>
        </div>
    </div>
);

const isDemoCase = caseData?.case_number?.startsWith('DEMO-');

    // NEW LOGIC: Enable button if they have uploaded a document, media, OR logged an activity
    const isReadyToComplete = documents.length > 0 || media.length > 0 || activity.length > 0;

    return (
        // Main container adjusted for better mobile padding
        <div className="p-4 pl-0 pr-0 sm:p-6 lg:p-8 max-w-7xl mx-auto bg-gray-50 min-h-screen">
            {/* Header Buttons */}
            <div className="flex justify-between items-start sm:items-center mb-8 pb-4 border-b border-gray-200 print:hidden flex-col sm:flex-row space-y-4 sm:space-y-0">
                <Link  to={roleName =="client" ? "/client/cases" : "/cases"} className="flex items-center space-x-1 text-gray-600 hover:text-gray-800">
                    <BiArrowBack className="w-5 h-5" /><span>Back to Cases</span>
                </Link>

                <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3 w-full sm:w-auto">

                    <button onClick={downloadPdf} className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 flex items-center justify-center space-x-1">
                        <BiDownload className="w-4 h-4" /><span>Download Formal Intake Document</span>
                    </button>

                    {/* {canEdit && (
                        <Link to={`/cases/edit/${caseData.id}`} className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 flex items-center justify-center space-x-1">
                            <BiEdit className="w-4 h-4" /><span>Edit Case</span>
                        </Link>
                    )} */}
                    {canEdit && !caseData.case_number?.startsWith('DEMO-') && (
                        <Link to={`/cases/edit/${caseData.id}`} className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 flex items-center justify-center space-x-1">
                            <BiEdit className="w-4 h-4" /><span>Edit Case</span>
                        </Link>
                    )}
                </div>
            </div>

            {/* Printable Content - Perfectly Balanced Single-Page Layout */}
            <div ref={printRef} className="p-6 sm:p-8 bg-white shadow-md rounded-lg border border-gray-200 print:shadow-none print:border-none print:p-2 print:m-0 text-sm sm:text-base">
                <header className="mb-4 pb-2.5 border-b-2 border-black text-center">
                    <h1 className="text-2xl sm:text-3xl font-black text-black uppercase tracking-wider">Service Intake Document</h1>
                    <p className="text-base sm:text-lg text-gray-800 mt-1 font-bold">{caseData.case_name}</p>
                    <p className="text-sm text-gray-600 mt-1">
                        Case No: <span className="font-mono font-bold text-gray-900">{caseData.case_number}</span> | Status: <span className="font-bold text-gray-900">{caseData.status}</span>
                    </p>
                </header>

                {/* Client Contact */}
                <DetailBox title="Client/Billing Contact Details">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
                        <DetailItem label="Client/Company" value={client_info.is_agency == 1 ? client_info.agency : client_info.full_name} />
                        <DetailItem label="Contact Person" value={client_info.contact_person} />
                        <DetailItem label="Phone Number" value={client_info.phone_number} />
                        <DetailItem label="Email Address" value={client_info.email_address} />
                        <DetailItem label="Preferred Contact" value={client_info.preferred_contact_method} />
                        <div className="md:col-span-2">
                            <DetailItem label="Billing Address" value={client_info.billing_address} />
                        </div>
                    </div>
                </DetailBox>

                {/* Case Info */}
                <DetailBox title="Case & Assignment Information">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
                        <DetailItem label="Client" value={caseData.client_full_name} />
                        <DetailItem label="Client Reference #" value={caseData.client_reference_number} />
                        <DetailItem label="Assigned Servers" value={agents || (Array.isArray(caseData.assigned_agents) ? caseData.assigned_agents.map(a => a.full_name).join(', ') : '') || 'Unassigned'} />
                        <DetailItem label="Server Work Status" value={formatLabel(caseData.server_work_status) || 'Unknown'} />
                        <DetailItem label="Service Type" value={caseData.service_type} />
                        <DetailItem label="Court Jurisdiction" value={caseData.court_jurisdiction} />
                        <DetailItem label="Service Due Date" value={caseData.due_date ? new Date(caseData.due_date).toLocaleDateString() : 'N/A'} />
                        <DetailItem label="Pages to Serve" value={caseData.number_of_pages} />
                    </div>
                </DetailBox>

                {/* Servee Info */}
                <DetailBox title="Defendant/Servee Location Information">
                    {servees.length > 0 ? (
                        <div className="space-y-2">
                            {servees.map((servee, index) => (
                                <div key={index} className="py-0.5">
                                    {servees.length > 1 && (
                                        <h4 className="font-bold text-sm sm:text-base text-black mb-1.5 border-b border-gray-200 pb-0.5">
                                            Servee #{index + 1}: {servee.full_name}
                                        </h4>
                                    )}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
                                        <DetailItem label="Full Name" value={servee.full_name} />
                                        <DetailItem label="Service Status" value={formatLabel(servee.status)} />
                                        <DetailItem label="Primary Address" value={`${servee.primary_address}${servee.apartment_unit ? ', Unit ' + servee.apartment_unit : ''}, ${servee.city_state_zip}`} />
                                        <DetailItem label="Alternate Address" value={servee.alternate_address} />
                                        <DetailItem label="Physical Description" value={servee.physical_description} />
                                        <DetailItem label="Vehicle Description" value={servee.vehicle_description} />
                                    </div>

                                    {/* Servee Attachments */}
                                    {servee.servee_attachments && servee.servee_attachments.length > 0 && (
                                        <div className="mt-1 pt-1 border-t border-gray-200 print:hidden">
                                            <h5 className="font-bold text-xs text-gray-700 mb-0.5">Servee Attachments:</h5>
                                            <ul className="space-y-0.5 list-disc list-inside">
                                                {servee.servee_attachments.map(attachment => (
                                                    <li key={attachment.id} className="flex justify-between items-center text-xs ml-4">
                                                        <span className="text-gray-800 break-all">{attachment.title}</span>
                                                        <a
                                                            href={`http://localhost:5000/api/files/download?path=${encodeURIComponent(attachment.file_path)}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="text-blue-600 hover:text-blue-900 font-semibold text-xs flex items-center space-x-1"
                                                        >
                                                            <BiDownload className="w-3 h-3" />
                                                            <span>Download</span>
                                                        </a>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="text-gray-500 italic text-xs">No servee location information found.</p>
                    )}
                </DetailBox>

                {/* Instructions */}
                <DetailBox title="Service Instructions & Case Description">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
                        <div>
                            <DetailItem label="Documents To Be Served" value={caseData.document_served || (documents.find(d => d.documents_to_be_served)?.documents_to_be_served) || 'N/A'} />
                            <DetailItem label="Service Instructions" value={caseData.service_instructions || (documents.find(d => d.service_instructions)?.service_instructions) || 'N/A'} />
                            <DetailItem label="Special Instructions" value={caseData.special_instructions || (documents.find(d => d.special_instructions)?.special_instructions) || 'N/A'} />
                        </div>

                        <div>
                            <div className="text-sm sm:text-base text-gray-800 mb-2.5 flex flex-col sm:flex-row leading-normal">
                                <span className="font-bold text-gray-700 mr-2 min-w-[145px] inline-block basis-full sm:basis-auto">Case Description:</span>
                                <span className="font-medium text-gray-900 whitespace-pre-line">{caseData.description || 'No detailed internal description provided.'}</span>
                            </div>
                            {Object.keys(custom_fields).length > 0 && (
                                <div className="mt-1">
                                    {Object.entries(custom_fields).map(([key, value]) => (
                                        <DetailItem key={key} label={key.replace(/_/g, ' ').split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')} value={value} />
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </DetailBox>

                {/* Field Service Record (For Manual Entry) */}
                <DetailBox title="Field Service Record (For Manual Entry)">
                    <p className="text-xs sm:text-sm italic text-gray-500 mb-3 print:text-gray-700">
                        *Please fill out this section manually while on-site if internet access is unavailable.*
                    </p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-5 text-sm sm:text-base text-black pb-1">
                        <div className="col-span-2 flex items-end">
                            <span className="font-bold mr-2 whitespace-nowrap text-sm sm:text-base">Date Served:</span>
                            <span className="border-b border-black w-full inline-block mb-1"></span>
                        </div>
                        <div className="col-span-2 flex items-end">
                            <span className="font-bold mr-2 whitespace-nowrap text-sm sm:text-base">Time Served:</span>
                            <span className="border-b border-black w-full inline-block mb-1"></span>
                        </div>

                        <div className="col-span-2 md:col-span-2 flex items-end">
                            <span className="font-bold mr-2 whitespace-nowrap text-sm sm:text-base">DOB:</span>
                            <span className="border-b border-black w-full inline-block mb-1"></span>
                        </div>
                        <div className="col-span-2 md:col-span-2 flex items-end">
                            <span className="font-bold mr-2 whitespace-nowrap text-sm sm:text-base">Sex/Race:</span>
                            <span className="border-b border-black w-full inline-block mb-1"></span>
                        </div>

                        <div className="col-span-1 flex items-end">
                            <span className="font-bold mr-2 whitespace-nowrap text-sm sm:text-base">Height:</span>
                            <span className="border-b border-black w-full inline-block mb-1"></span>
                        </div>
                        <div className="col-span-1 flex items-end">
                            <span className="font-bold mr-2 whitespace-nowrap text-sm sm:text-base">Weight:</span>
                            <span className="border-b border-black w-full inline-block mb-1"></span>
                        </div>
                        <div className="col-span-1 flex items-end">
                            <span className="font-bold mr-2 whitespace-nowrap text-sm sm:text-base">Hair:</span>
                            <span className="border-b border-black w-full inline-block mb-1"></span>
                        </div>
                        <div className="col-span-1 flex items-end">
                            <span className="font-bold mr-2 whitespace-nowrap text-sm sm:text-base">Eyes:</span>
                            <span className="border-b border-black w-full inline-block mb-1"></span>
                        </div>

                        <div className="col-span-2 md:col-span-4 flex items-end">
                            <span className="font-bold mr-2 whitespace-nowrap text-sm sm:text-base">Distinguishing Marks/Tattoos:</span>
                            <span className="border-b border-black w-full inline-block mb-1"></span>
                        </div>

                        <div className="col-span-2 flex items-end">
                            <span className="font-bold mr-2 whitespace-nowrap text-sm sm:text-base">Military Service (Y/N/Unknown):</span>
                            <span className="border-b border-black w-full inline-block mb-1"></span>
                        </div>
                        <div className="col-span-2 flex items-end">
                            <span className="font-bold mr-2 whitespace-nowrap text-sm sm:text-base">Glasses (Y/N):</span>
                            <span className="border-b border-black w-full inline-block mb-1"></span>
                        </div>
                    </div>
                </DetailBox>

                {/* Footer */}
                <div className="mt-6 pt-3 border-t border-gray-300 text-xs sm:text-sm text-gray-500 flex justify-between items-center">
                    <span>Confidential — Internal Process Service Record</span>
                    <span>Generated: {new Date().toLocaleDateString()} | System ID: {caseData.id}</span>
                </div>
            </div>

            {isDemoCase && <OnboardingGuide step="3" title="Generate an Affidavit" description="Once you have logged an attempt, generate a formal affidavit/report here." />}
            <AffidavitListComponent
                caseId={id}
                key={affidavitUpdateTrigger}
            />

            {/* Step 2: Servee Action */}
            {isDemoCase && <OnboardingGuide step="2" title="Log a Service Attempt" description="Click the action button below to log a successful or failed service attempt." />}
            <ServeeActionComponent
                caseId={id}
                onAffidavitCreated={handleAffidavitCreated}
            />

            {planType != 'standard' && (
                <CaseMessaging caseId={id} isPiCase={false} />
            )}

            {/* Web-only section */}
            {/* <div className="mt-8 pt-8 border-t border-gray-200 print:hidden"> */}


            {/* Web-only section */}
            <div className="mt-8 pt-8 border-t border-gray-200 print:hidden">
                {/* <h2 className="text-xl sm:text-2xl font-extrabold text-gray-900 mb-5 flex items-center space-x-2">
                    <BiFile className="w-6 h-6" /> <span>Case Files & Activity (Web Only)</span>
                </h2> */}

                <h2 className="text-xl sm:text-2xl font-extrabold text-gray-900 mb-5 flex items-center space-x-2 flex-wrap gap-2">
                    <div className="flex items-center space-x-2">
                        <BiFile className="w-6 h-6" /> <span>Case Files & Activity</span>
                    </div>
                    {/* Step 1: Document Upload */}
                    {isDemoCase && (
                        <span className="bg-indigo-600 text-white px-3 py-1 rounded-full text-xs font-bold animate-pulse shadow-sm">
                            Step 1: Upload a Document Here
                        </span>
                    )}
                </h2>

                {/* {canUpload && <DocumentUpload caseId={id} onUploadSuccess={handleUploadSuccess} />} */}
                {canUpload && <DocumentUpload caseId={id} isPiCase={false} onUploadSuccess={handleUploadSuccess} />}

                {/* Adjusted grid to stack columns on small screens */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
                    {/* Documents */}
                    <div className="bg-white p-5 rounded-lg shadow-md border border-gray-100">
                        <h3 className="font-semibold text-lg text-gray-800 mb-3">Documents ({documents.length})</h3>
                        <ul className="space-y-2">
                            {documents.length > 0 ? (
                                documents.map(doc => (
                                    <li key={doc.id} className="flex flex-wrap justify-between items-center p-3 bg-gray-50 rounded-md border border-gray-200">
                                        <span className="font-medium text-gray-700 text-sm break-all mb-1 sm:mb-0">{doc.file_name}</span>
                                        <button onClick={() => handleDownload(doc.id, doc.file_name, 'documents')} className="text-blue-600 hover:text-blue-900 font-semibold text-xs flex items-center space-x-1">
                                            <BiDownload className="w-4 h-4" /><span>Download</span>
                                        </button>
                                    </li>
                                ))
                            ) : (
                                <p className="text-gray-500 text-sm">No official documents uploaded yet.</p>
                            )}
                        </ul>
                    </div>

                    {/* Media */}
                    <div className="bg-white p-5 rounded-lg shadow-md border border-gray-100">
                        <h3 className="font-semibold text-lg text-gray-800 mb-3">Media (Photos/Videos) ({media.length})</h3>
                        <ul className="space-y-2">
                            {media.length > 0 ? (
                                media.map(mediaItem => (
                                    <li key={mediaItem.id} className="flex flex-wrap justify-between items-center p-3 bg-gray-50 rounded-md border border-gray-200">
                                        <span className="font-medium text-gray-700 text-sm break-all mb-1 sm:mb-0">{mediaItem.file_name}</span>
                                        <button onClick={() => handleDownload(mediaItem.id, mediaItem.file_name, 'media')} className="text-blue-600 hover:text-blue-900 font-semibold text-xs flex items-center space-x-1">
                                            <BiDownload className="w-4 h-4" /><span>Download</span>
                                        </button>
                                    </li>
                                ))
                            ) : (
                                <p className="text-gray-500 text-sm">No supporting media found.</p>
                            )}
                        </ul>
                    </div>

                    

                    {/* Activity */}
                    <div className="bg-white p-5 rounded-lg shadow-md border border-gray-100">
                        <h3 className="font-semibold text-lg text-gray-800 mb-3">Activity Log ({activity.length})</h3>
                        <div className="max-h-96 overflow-y-auto space-y-3">
                            {activity.length > 0 ? (
                                activity.map(log => (
                                    <div key={log.id} className="p-3 bg-gray-50 rounded-md text-sm text-gray-700 border border-gray-200">
                                        <p className="font-medium">{log.description}</p>
                                        <p className="text-xs text-gray-500 mt-1">{new Date(log.created_at).toLocaleString()}</p>
                                        {log.user_name && <p className="text-xs text-gray-500">By: {log.user_name}</p>}
                                    </div>
                                ))
                            ) : (
                                <p className="text-gray-500 text-sm">No recent activity found.</p>
                            )}
                        </div>
                    </div>
                </div>
                     <div className="w-full flex justify-end  mt-6">
                    {/* Complete Onboarding Button */}
                    {isDemoCase && (
                        <div className="flex flex-col sm:flex-row items-center space-y-2 sm:space-y-0 sm:space-x-2 w-full sm:w-auto">
                            <span className="bg-indigo-100 text-indigo-800 text-xs font-bold px-3 py-1 rounded-full border border-indigo-200 whitespace-nowrap">
                                Step 4: Finish!
                            </span>
                            <div className="tour-complete-onboarding w-full sm:w-auto">
                                <button 
                                    onClick={() => {
                                        trackOnboardingEvent('workflow_reviewed');
                                        // Open the custom popup instead of navigating instantly!
                                        setShowSuccessModal(true); 
                                    }} 
                                    // FIX: Ensure the button is NEVER disabled during the tour!
                                    disabled={!runTour && documents.length === 0}
                                    title={(!runTour && documents.length === 0) ? "Please upload a document to complete onboarding" : "Complete Onboarding"}
                                    className={`w-full sm:w-auto px-4 py-2 text-sm font-medium text-white rounded-md flex items-center justify-center space-x-1 transition-colors ${
                                        (!runTour && documents.length === 0) ? 'bg-gray-400 cursor-not-allowed opacity-75' : 'bg-indigo-600 hover:bg-indigo-700 shadow-md'
                                    }`}
                                >
                                    <span>✅ Complete Onboarding</span>
                                </button>
                            </div>
                        </div>
                    )}
                    </div>
            </div>
            {showSuccessModal && (
                <div className="fixed inset-0 z-[60000] flex items-center justify-center bg-black bg-opacity-60 transition-opacity">
                    <div className="bg-white rounded-xl shadow-2xl p-8 max-w-md w-full text-center transform transition-all scale-100 opacity-100">
                        <div className="mx-auto flex items-center justify-center h-20 w-20 rounded-full bg-green-100 mb-6 shadow-sm">
                            <BiCheckCircle className="h-12 w-12 text-green-600" />
                        </div>
                        <h3 className="text-3xl font-extrabold text-gray-900 mb-3">Onboarding Complete!</h3>
                        <p className="text-gray-600 mb-8 text-md leading-relaxed">
                            🎉 Awesome job! You have successfully reviewed the complete workflow. Your trial activation is now fully set up.
                        </p>
                        <button
                            onClick={handleFinalRedirect}
                            className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-3.5 px-4 rounded-lg shadow-md transition-all hover:shadow-lg focus:outline-none focus:ring-4 focus:ring-green-300"
                        >
                            OK, Take me to my Dashboard
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CaseDetail;
